import { accountFields, PRIMARY_FIELDS, type FieldInfo } from '@tellvo/setup-protocol';

/**
 * The editor's data model: which fields exist (from the schema), what the phone last
 * confirmed, and how a keystroke becomes a JSON Merge Patch. Pure, for tests.
 */
export class FormModel {
  readonly fields: FieldInfo[];
  /** The phone's last confirmed account, by key. */
  private confirmed: Record<string, unknown> = {};

  constructor(schema: unknown) {
    this.fields = accountFields(schema);
  }

  get primary(): FieldInfo[] {
    return PRIMARY_FIELDS.map((key) => this.fields.find((f) => f.key === key)).filter((f): f is FieldInfo => f !== undefined);
  }

  get advanced(): FieldInfo[] {
    return this.fields.filter((f) => !(PRIMARY_FIELDS as readonly string[]).includes(f.key));
  }

  applyState(account: Record<string, unknown>): void {
    this.confirmed = { ...account };
  }

  value(key: string): unknown {
    return this.confirmed[key];
  }

  /** The merge patch for a single edit: empty text clears an optional field (`null`). */
  patchFor(field: FieldInfo, raw: string | boolean | string[]): Record<string, unknown> | null {
    let value: unknown;
    switch (field.type) {
      case 'integer': {
        const text = String(raw).trim();
        if (text === '') { value = field.required ? undefined : null; break; }
        const n = Number(text);
        if (!Number.isInteger(n)) return null;
        if (field.minimum !== undefined && n < field.minimum) return null;
        if (field.maximum !== undefined && n > field.maximum) return null;
        value = n;
        break;
      }
      case 'boolean':
        value = Boolean(raw);
        break;
      case 'codecs':
        if (!Array.isArray(raw) || raw.length === 0) return null;
        value = raw;
        break;
      case 'enum':
        if (!field.enumValues?.includes(String(raw))) return null;
        value = String(raw);
        break;
      case 'string': {
        const text = normalize(field, String(raw));
        if (text !== '' && field.pattern && !new RegExp(field.pattern).test(text)) return null;
        value = text === '' && !field.required && !field.sensitive ? null : text;
        break;
      }
    }
    if (value === undefined) return null;
    if (value === this.confirmed[field.key]) return null;
    return { [field.key]: value };
  }

  /** Text the field cannot take (the patch is null for it, not because nothing changed). */
  isInvalid(field: FieldInfo, raw: string): boolean {
    const text = field.type === 'string' ? normalize(field, raw) : raw.trim();
    if (text === '') return false;
    if (field.type === 'integer') return this.patchFor(field, raw) === null && Number(raw) !== this.confirmed[field.key];
    if (field.type === 'string' && field.pattern) return !new RegExp(field.pattern).test(text);
    return false;
  }

  /** The keys of what the phone still needs before it can save; the page words them. */
  missing(hasPassword: boolean): string[] {
    const missing = this.fields
      .filter((f) => f.required && !f.sensitive && !String(this.confirmed[f.key] ?? '').trim())
      .map((f) => f.key);
    if (!hasPassword) missing.push('password');
    return missing;
  }
}

/** A fingerprint as viewers print it ("AB:CD …") becomes the schema's lower-case hex. */
function normalize(field: FieldInfo, text: string): string {
  return field.key === 'trustedCertificateSha256' ? text.replace(/[\s:]/g, '').toLowerCase() : text;
}

/**
 * Choosing a transport: TLS turns call encryption on "when available" (tellvo-voip-client #75)
 * when it is off, since TLS protects the keys SRTP sends in the signalling. A "required" stays,
 * and nothing changes on the way back.
 */
export function transportPatch(account: Record<string, unknown>, transport: string): Record<string, unknown> {
  const srtp = account['srtp'] ?? 'disabled';
  const wasTls = (account['transport'] ?? 'udp') === 'tls';
  return transport === 'tls' && !wasTls && srtp === 'disabled' ? { transport, srtp: 'optional' } : { transport };
}
